/** ------------------------------------------------------------------------- *
 * Overflow Tables *
 * -------------------------------------------------------------------------- *
 * @module Tables
 * @desc
 * Wraps tables in an overflowing element and tracks the scroll position in
 * order to display a gradient on either side to indicate there is hidden
 * content in either direction.
 *
 * -------------------------- *
 * Trigger Element Attributes *
 * -------------------------- *
 */

const settings = {
    // The default DOMString selector for initializing the module
    selector: '.js-table',
    // The default classname for the wrapper element
    wrapper_class: 'wp-block-table__wrapper',
    // The state classes for the container element
    state_classes: {
        left: 'has-overflow-left',
        right: 'has-overflow-right',
    },
    // Threshold for displaying at the start and end of scrolling
    threshhold: 20,
};

/**
 * @property debounce
 * @function
 * @param {Function} [callback] - The callback function
 * @param {Number} [timeout] - The number of milliseconds to debounce by
 * @desc Debounces a function to improve performance
 */

function debounce(callback, timeout = 250) {
    let timer;

    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => callback.apply(this, args), timeout);
    };
}

/** ------------------- *
 * Overflow Table Class *
 * -------------------- *
 * @class OverflowTable
 * @desc Wraps a table element and adds classes based on scroll position
 */

class OverflowTable {
    /**
     * @constructor
     * @param {Element} el - The container element for the table
     * @param {String} wrapper_class - The classname for the wrapper element
     */

    constructor(el, wrapper_class) {
        // Wrap the contents
        const html = el.innerHTML;
        el.innerHTML = `<div class="${wrapper_class}">${html}</div>`;

        this.el = el;
        this.wrapper_el = el.querySelector(`.${wrapper_class}`);

        // Update the initial state
        this.updateState();

        // Update when the container is scrolled
        this.wrapper_el.addEventListener('scroll', debounce(() => this.updateState()));

        // Update when the window is resized
        window.addEventListener('resize', () => this.updateState());
    }

    /**
     * @property OverflowTable.updateState
     * @method
     * @desc Adds the state classes if content is scrollable
     */

    updateState() {
        const scroll_left = this.wrapper_el.scrollLeft;
        const scroll_position = this.el.clientWidth + scroll_left;

        // Check for content on the left
        if (scroll_left >= settings.threshhold) {
            this.el.classList.add('has-overflow-left');
        } else {
            this.el.classList.remove('has-overflow-left');
        }

        // Check for content on the right
        if (scroll_position <= this.wrapper_el.scrollWidth - settings.threshhold) {
            this.el.classList.add('has-overflow-right');
        } else {
            this.el.classList.remove('has-overflow-right');
        }
    }
}

/** ------------ *
 * Module Export *
 * ------------- *
 */

const Tables = {
    /**
     * @property Tables.selector
     * @desc The default DOMString selector for initializing the module
     */

    selector: settings.selector,

    /**
     * @property Tables.init
     * @method
     * @param {String} [selector] - The DOMString selector for the toggle button(s)
     * @param {String} [wrapper_class] - The classname for the wrapper element
     * @desc Intializes the module
     */

    init(selector = settings.selector, wrapper_class = settings.wrapper_class) {
        // Convert NodeList to Array
        const tables = document.querySelectorAll(selector);
        const table_arr = Array.from(tables);

        // Map the new Class object to the element
        table_arr.forEach((el) => {
            new OverflowTable(el, wrapper_class);
        });
    },
};

export default Tables;
